import React, { ChangeEvent, InputHTMLAttributes, useMemo } from 'react';

import PropTypes from 'prop-types';

import { useControlled } from '@adsk/alloy-react-helpers';
import { CheckmarkIcon, MinusIcon } from '@adsk/alloy-react-icon';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

export type Indeterminate = 'indeterminate';
export type CheckboxState = true | false | Indeterminate;

const CONTROL_SIZE = 16;
const CONTROL_BORDER_WIDTH = 1;
const CLICK_TARGET_SIZE = 20;
const CLICK_TARGET_OFFSET = (CONTROL_SIZE - CLICK_TARGET_SIZE) / 2;

const INDETERMINATE = 'indeterminate' as const;

const CheckboxStates = [true, false, INDETERMINATE] as const;

const mapStateToAriaChecked = (state: CheckboxState) => {
  if (state === INDETERMINATE) return 'mixed';
  return state;
};

const mapStateToIconRenderer = (state: CheckboxState) => {
  if (state === true) return CheckmarkIcon.renderIcon;
  if (state === INDETERMINATE) return MinusIcon.renderIcon;
  return () => null;
};

const checkIndeterminateState = (state: CheckboxState) => {
  // Expected behavior when using indeterminate checkbox state.
  if (state === INDETERMINATE) {
    return true;
  }

  return !state;
};

export type CheckboxProps = Omit<
  StylableComponent<HTMLDivElement>,
  'defaultChecked' | 'onChange'
> & {
  checked?: CheckboxState;
  defaultChecked?: CheckboxState;
  disabled?: boolean;
  id?: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  name?: string;
  onChange?: (
    state: CheckboxState,
    event: ChangeEvent<HTMLInputElement>,
  ) => void;
  readOnly?: boolean;
  'data-testid'?: string;
};

/**
 * A Checkbox
 * Provides a control to select from a list of non-exclusive options.
 * Checking the box should enable or select the active state.
 * (Don't phrase check boxes with a negative and require checking a box to turn something off.)
 * The rest of the props are propagated to the input element.
 *
 * When `readOnly` is true the hidden input is disabled to avoid accessibility
 * violations when nested in another input (e.g. a Dropdown MenuItem). The
 * checkbox remains visually enabled but functionally disabled.
 */
const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      style,
      className,
      id,
      name,
      checked,
      disabled = false,
      defaultChecked = false,
      onChange: onChangeProp,
      readOnly,
      inputProps = {},
      'data-testid': dataTestid,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const checkboxTokens = theme.tokens?.component?.checkbox;

    const [value, handleOnChange] = useControlled<CheckboxState>(
      defaultChecked,
      checked,
      onChangeProp,
    );

    const onChangeWrapper = (e: ChangeEvent<HTMLInputElement>) =>
      handleOnChange(checkIndeterminateState(value), e);

    const onChange = readOnly
      ? undefined
      : (e: ChangeEvent<HTMLInputElement>) => {
          !disabled && onChangeWrapper(e);
        };

    const notUnchecked = value !== false;

    const { baseStyles, checkedStateTokens, disabledStateTokens } =
      useMemo(() => {
        const checkedStateTokens =
          checkboxTokens?.checked?.[notUnchecked ? 'true' : 'false'];
        const disabledStateTokens =
          checkedStateTokens?.disabled?.[disabled ? 'true' : 'false'];

        return {
          baseStyles: checkboxTokens?._,
          checkedStateTokens,
          disabledStateTokens,
        };
      }, [checkboxTokens, notUnchecked, disabled]);

    const { style: inputStyle, ...rest } = inputProps;

    return (
      <div
        className={className}
        css={[
          baseStyles,
          checkedStateTokens?._,
          disabledStateTokens,
          {
            display: 'block',
            height: CONTROL_SIZE,
            width: CONTROL_SIZE,
            borderWidth: notUnchecked ? 0 : CONTROL_BORDER_WIDTH,
            pointerEvents: disabled || readOnly ? 'none' : 'auto',
          },
          style,
        ]}
        {...props}
      >
        {notUnchecked && mapStateToIconRenderer(value)({ size: CONTROL_SIZE })}
        <input
          ref={ref}
          id={id}
          name={name}
          value={value.toString()}
          type="checkbox"
          onChange={onChange}
          readOnly={readOnly}
          checked={!!value}
          aria-checked={mapStateToAriaChecked(value)}
          disabled={disabled || readOnly}
          data-testid={dataTestid}
          css={[
            {
              pointerEvents: disabled || readOnly ? 'none' : 'auto',
              appearance: 'none',
              cursor: 'inherit',
              display: 'block',
              position: 'relative',
              opacity: 0,
              width: CLICK_TARGET_SIZE,
              height: CLICK_TARGET_SIZE,
              top: notUnchecked
                ? CLICK_TARGET_OFFSET - CONTROL_SIZE
                : CLICK_TARGET_OFFSET - CONTROL_BORDER_WIDTH,
              left: notUnchecked
                ? CLICK_TARGET_OFFSET
                : CLICK_TARGET_OFFSET - CONTROL_BORDER_WIDTH,
              margin: 0,
              padding: 0,
            },
            { ...inputStyle },
          ]}
          {...rest}
        />
      </div>
    );
  },
);

Checkbox.displayName = 'Checkbox';

Checkbox.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The id of the checkbox, used for the label */
  id: PropTypes.string,
  /** The name for the checkbox input */
  name: PropTypes.string,
  /** The value of the checkbox */
  checked: PropTypes.oneOf(CheckboxStates),
  /** The initial value of the checkbox, this will be read only if `checked` is undefined */
  defaultChecked: PropTypes.oneOf(CheckboxStates),
  /** onChange handler for the checkbox input */
  onChange: PropTypes.func,
  /** Disables propagation stop */
  readOnly: PropTypes.bool,
  /** Disable checkbox functionality */
  disabled: PropTypes.bool,
  /** Props passed to the input element */
  inputProps: PropTypes.object,
  /** data-testid passed to the input element */
  'data-testid': PropTypes.string,
};

export default Object.assign(Checkbox, { INDETERMINATE });
